import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import {
  getDiseasesAction,
  deleteDiseaseAction,
} from "../../../store/actions/DiseasesAction";
import Loader from "../Common/Loader";

const Disease = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const diseases = useSelector((state) => state.diseases.diseases || []);
  const num_rows = useSelector((state) => state.diseases.num_rows || 0);

  const [searchQueries, setSearchQueries] = useState({
    disease_name: "",
    disease_status: "",
  });

  const [sortField, setSortField] = useState("disease_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  useEffect(() => {
    const fetchDiseases = async () => {
      setLoading(true);
      setError("");
      try {
        await fetchDiseasesFromServer();
      } catch (err) {
        console.error("Failed to fetch diseases:", err);
        setError("Failed to load diseases.");
      } finally {
        setLoading(false);
      }
    };

    fetchDiseases();
  }, [dispatch, location.key]);

  const fetchDiseasesFromServer = async (
    search_by = "",
    search_keyword = "",
    pagination = true,
    pageid = 0,
    sort_by = "disease_name",
    sort_order = "asc"
  ) => {
    try {
      await dispatch(
        getDiseasesAction(
          pagination,
          pageid,
          search_by,
          search_keyword,
          sort_by,
          sort_order
        )
      );
    } catch (err) {
      console.error("Failed to fetch diseases:", err);
      setError("Failed to load diseases.");
    }
  };

  const handleSearch = (e, field) => {
    const keyword = e.target.value;

    // Clear all search queries first, then set the current field
    const clearedQueries = {
      disease_name: "",
      disease_status: "",
      partner_required: "",
    };

    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0);

    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);
    fetchDiseasesFromServer(field, keyword, true, 0, sortField, sortOrder);
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);

    fetchDiseasesFromServer(
      activeSearchField,
      activeSearchKeyword,
      true,
      0,
      field,
      order
    );
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchDiseasesFromServer(
      activeSearchField,
      activeSearchKeyword,
      true,
      newPage,
      sortField,
      sortOrder
    );
  };

  const handleEdit = (disease) => {
    console.log("dies:", disease);
    navigate(`/diseases/update-diseases/${disease.disease_id}`, {
      state: { disease },
    });
  };

  const handleDelete = (disease) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this disease!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setLoading(true);
          await dispatch(deleteDiseaseAction(disease.disease_id));
          // Refresh the current page after deletion
          await fetchDiseasesFromServer(
            activeSearchField,
            activeSearchKeyword,
            true,
            currentPage,
            sortField,
            sortOrder
          );
        } catch (err) {
          setError("Failed to delete disease");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const totalPages = Math.ceil(num_rows / rowsPerPage);
  const startEntry = num_rows === 0 ? 0 : currentPage * rowsPerPage + 1;
  const endEntry = Math.min((currentPage + 1) * rowsPerPage, num_rows);

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link
            to="/diseases/add-diseases"
            className="btn btn-primary btn-rounded"
          >
            + Add Disease
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th
                            onClick={() => handleSort("disease_name")}
                            style={{ cursor: "pointer" }}
                          >
                            Disease Name{" "}
                            {sortField === "disease_name" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.disease_name}
                              onChange={(e) => handleSearch(e, "disease_name")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Disease Name"
                            />
                          </th>
                          <th
                            onClick={() => handleSort("disease_status")}
                            style={{ cursor: "pointer" }}
                          >
                            Status{" "}
                            {sortField === "disease_status" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.disease_status}
                              onChange={(e) => handleSearch(e, "disease_status")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Status"
                            />
                          </th>
                          <th>
                            Partner Required
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.partner_required}
                              onChange={(e) => handleSearch(e, "partner_required")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Yes / No"
                            />
                          </th>


                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diseases.map((disease) => (
                          <tr key={disease.disease_id}>
                            <td>{disease.disease_name}</td>
                            <td>
                              <span
                                className={`badge px-3 py-1 rounded-pill text-white ${disease.disease_status.toLowerCase() === "active"
                                    ? "bg-success"
                                    : "bg-danger"
                                  }`}
                              >
                                {disease.disease_status}
                              </span>
                            </td>
                            <td>{disease.partner_required}</td>

                            <td>
                              <div className="d-flex">
                                <span
                                  className="edit me-4"
                                  onClick={() => handleEdit(disease)}
                                  style={{ cursor: "pointer" }}
                                  title="Edit Disease"
                                >
                                  <i className="fa fa-pencil-alt text-success"></i>
                                </span>
                                <span
                                  className="delete me-2"
                                  onClick={() => handleDelete(disease)}
                                  style={{ cursor: "pointer" }}
                                  title="Delete Disease"
                                >
                                  <i className="fa fa-trash text-danger"></i>
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {diseases.length === 0 && (
                          <tr>
                            <td colSpan="3" className="text-center">
                              No diseases found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      Showing {startEntry} to {endEntry} of {num_rows} entries
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      {(() => {
                        const pages = [];
                        const maxPagesToShow = 5;
                        const startPage = Math.max(
                          0,
                          Math.min(currentPage - 2, totalPages - maxPagesToShow)
                        );
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => handlePageChange(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>
                          );
                          if (startPage > 1) {
                            pages.push(
                              <span
                                key="dots1"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                        }

                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i;
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>
                          );
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span
                                key="dots2"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => handlePageChange(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                      <button
                        onClick={() =>
                          handlePageChange(Math.min(currentPage + 1, totalPages - 1))
                        }
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor:
                            currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Disease;
